/*
 The MIT License (MIT)

 Copyright (c) 2017-2018 Stefano Cappa (Ks89)

 Permission is hereby granted, free of charge, to any person obtaining a copy
 of this software and associated documentation files (the "Software"), to deal
 in the Software without restriction, including without limitation the rights
 to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 copies of the Software, and to permit persons to whom the Software is
 furnished to do so, subject to the following conditions:

 The above copyright notice and this permission notice shall be included in all
 copies or substantial portions of the Software.

 THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 FITNESS FOR A PARTICULAR PURPOSE AND NON INFRINGEMENT. IN NO EVENT SHALL THE
 AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 SOFTWARE.
 */

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SecurityContext,
  SimpleChange,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";

import {
  ButtonEvent,
  ButtonsConfig,
  ButtonType,
} from "../../model/buttons-config.interface";
import { Image, ImageModalEvent } from "../../model/image.class";
import { Action } from "../../model/action.enum";
import { KeyboardConfig } from "../../model/keyboard-config.interface";
import { PreviewConfig } from "../../model/preview-config.interface";
import {
  SidePreviewsConfig,
  SlideConfig,
} from "../../model/slide-config.interface";
import { AccessibilityConfig } from "../../model/accessibility.interface";
import { KeyboardService } from "../../services/keyboard.service";
import {
  GalleryService,
  InternalGalleryPayload,
} from "../../services/gallery.service";
import { DotsConfig } from "../../model/dots-config.interface";
import {
  CurrentImageComponent,
  ImageLoadEvent,
} from "../current-image/current-image.component";
import { InternalLibImage } from "../../model/image-internal.class";
import {
  AdvancedLayout,
  PlainGalleryConfig,
} from "../../model/plain-gallery-config.interface";
import { KS_DEFAULT_ACCESSIBILITY_CONFIG } from "../accessibility-default";
import { CurrentImageConfig } from "../../model/current-image-config.interface";
import { getIndex } from "../../utils/image.util";

import { Subscription } from "rxjs";
import { IdValidatorService } from "../../services/id-validator.service";
import { InteractionEvent } from "../../model/interaction-event.interface";
import { PlayConfig } from "../../model/play-config.interface";
import { SelectedImage } from "../../model/selectedImage.interface";
import { DomSanitizer } from "@angular/platform-browser";
import { IComment } from "../comments/comment/comment.component";
import { ModalGalleryService } from "./modal-gallery.service";

/**
 * Main Component of this library with both the plain and modal galleries.
 */
@Component({
  selector: "ks-modal-gallery",
  exportAs: "ksModalGallery",
  styleUrls: ["modal-gallery.scss", "modal-gallery-modern.scss"],
  templateUrl: "modal-gallery.html",
  //changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalGalleryComponent implements OnInit, OnDestroy, OnChanges {
  @Input() user: any;
  /**
   * DataSource for comments
   */
  @Input()
  commentsDataSource: any;
  /**
   * Unique id (>=0) of the current instance of this library. This is useful when you are using
   * the service to call modal gallery without open it manually.
   */
  @Input()
  id: number;
  /**
   * Array of `Image` that represent the model of this library with all images, thumbs and so on.
   */
  @Input()
  modalImages: Image[];
  /**
   * Object of type `ButtonsConfig` to show/hide buttons.
   */
  @Input()
  buttonsConfig: ButtonsConfig;
  /**
   * Boolean to enable modal-gallery close behaviour when clicking
   * on the semi-transparent background. Enabled by default.
   */
  @Input()
  enableCloseOutside = true;
  /**
   * Interface to configure current image in modal-gallery.
   * For instance you can disable navigation on click on current image (enabled by default).
   */
  @Input()
  currentImageConfig: CurrentImageConfig;
  /**
   * Object of type `DotsConfig` to init DotsComponent's features.
   * For instance, it contains a param to show/hide dots.
   */
  @Input()
  dotsConfig: DotsConfig;
  /**
   * Object of type `PreviewConfig` to init PreviewsComponent's features.
   * For instance, it contains a param to show/hide previews.
   */
  @Input()
  previewConfig: PreviewConfig;
  /**
   * Object of type `SlideConfig` to init side previews and `infinite sliding`.
   */
  @Input()
  slideConfig: SlideConfig;
  /**
   * Object of type `AccessibilityConfig` to init custom accessibility features.
   * For instance, it contains titles, alt texts, aria-labels and so on.
   */
  @Input()
  accessibilityConfig: AccessibilityConfig = KS_DEFAULT_ACCESSIBILITY_CONFIG;
  /**
   * Object of type `KeyboardConfig` to assign custom keys to ESC, RIGHT and LEFT keyboard's actions.
   */
  @Input()
  keyboardConfig: KeyboardConfig;
  /**
   * Object of type `PlainGalleryConfig` to configure the plain gallery.
   */
  @Input()
  plainGalleryConfig: PlainGalleryConfig;

  @Input() selectedImages: number[];

  @Input() infoTemplate;

  @Input() isCheckable: boolean = true;

  /**
   * Output to emit an event when the modal gallery is closed.
   */
  @Output()
  close: EventEmitter<ImageModalEvent> = new EventEmitter<ImageModalEvent>();
  /**
   * Output to emit an event when an image is changed.
   */
  @Output()
  show: EventEmitter<ImageModalEvent> = new EventEmitter<ImageModalEvent>();
  /**
   * Output to emit an event when the current image is the first one.
   */
  @Output()
  firstImage: EventEmitter<ImageModalEvent> =
    new EventEmitter<ImageModalEvent>();
  /**
   * Output to emit an event when the current image is the last one.
   */
  @Output()
  lastImage: EventEmitter<ImageModalEvent> =
    new EventEmitter<ImageModalEvent>();
  /**
   * Output to emit an event when the modal gallery is closed.
   */
  @Output()
  hasData: EventEmitter<ImageModalEvent> = new EventEmitter<ImageModalEvent>();
  /**
   * Output to emit an event when a button is clicked, but before that the action is triggered.
   */
  @Output()
  buttonBeforeHook: EventEmitter<ButtonEvent> = new EventEmitter<ButtonEvent>();
  /**
   * Output to emit an event when a button is clicked, but after that the action is triggered.
   */
  @Output()
  buttonAfterHook: EventEmitter<ButtonEvent> = new EventEmitter<ButtonEvent>();
  /**
   * Output to emit an event when someone clicks either an arrow of modal gallery or also in previews.
   */
  @Output()
  arrow: EventEmitter<InteractionEvent> = new EventEmitter<InteractionEvent>();

  @Output() selectImage = new EventEmitter<SelectedImage>();

  @Output() info = new EventEmitter<ButtonEvent>();

  @Output() public longerTask: EventEmitter<IComment> = new EventEmitter();

  /**
   * Reference to the CurrentImageComponent to invoke methods on it.
   */
  @ViewChild(CurrentImageComponent)
  currentImageComponent;

  /**
   * Boolean that it is true if the modal gallery is visible. False by default.
   */
  opened = false;
  /**
   * Boolean that it is true if the modal gallery is visible. False by default.
   */
  openedInfoBlock = false;
  /**
   * Boolean to open the modal gallery. False by default.
   */
  showGallery = false;
  /**
   * Array of `InternalLibImage` representing the model of this library with all images, thumbs and so on.
   */
  images: InternalLibImage[];
  /**
   * `Image` that is visible right now.
   */
  currentImage: InternalLibImage;
  /**
   * Object of type `SlideConfig` passed to currentImage and used to handle play/stop features. This field is initialized
   * applying transformations, default values and so on to the input of the same type.
   */
  configSlide: SlideConfig;

  infoBlockContent;

  private galleryServiceNavigateSubscription: Subscription;
  private galleryServiceCloseSubscription: Subscription;
  private galleryServiceUpdateSubscription: Subscription;
  private galleryServiceAutoPlaySubscription: Subscription;

  /**
   * DOM portal container appended to document.body.
   * Used to move the overlay and modal wrapper outside of any
   * stacking-context / overflow containers so that position:fixed
   * works correctly in Safari.
   */
  private portalContainer: HTMLElement | null = null;

  /**
   * Constructor with the injection of ´KeyboardService´, an object to support Server-Side Rendering and other useful services.
   */
  constructor(
    private keyboardService: KeyboardService,
    private galleryService: GalleryService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private changeDetectorRef: ChangeDetectorRef,
    private modalGalleryService: ModalGalleryService,
    private idValidatorService: IdValidatorService,
    private sanitizer: DomSanitizer,
    private elRef: ElementRef
  ) {}

  /**
   * HostListener to catch browser's back button and destroy the gallery.
   * This prevents weired behaviour about scrolling.
   * Added to fix this issue: https://github.com/Ks89/angular-modal-gallery/issues/159
   */
  @HostListener("window:popstate", ["$event"])
  onPopState(e: Event) {
    this.closeGallery();
  }

  /**
   * Method ´ngOnChanges´ to re-init images if input is changed.
   * This is an Angular's lifecycle hook, so its called automatically by Angular itself.
   * In particular, it's called before `ngOnInit()` and whenever one or more data-bound input properties change.
   * @param changes `SimpleChanges` object of current and previous property values provided by Angular.
   */
  ngOnChanges(changes: SimpleChanges) {
    // console.log('changes modal', changes);
    const imagesChange: SimpleChange = changes.modalImages;
    const plainGalleryConfigChange: SimpleChange = changes.plainGalleryConfig;
    const infoTemplate = changes.infoTemplate;

    if (infoTemplate && !infoTemplate.firstChange) {
      if (
        infoTemplate.currentValue?.photoInfo.img.id !==
        infoTemplate.previousValue?.photoInfo.img.id
      ) {
        this.modalGalleryService.clearPhotoToSend();
        this.modalGalleryService.onCheckedSetTaskMerch(false);
      }
    }

    if (
      imagesChange &&
      !imagesChange.firstChange &&
      imagesChange.previousValue !== imagesChange.currentValue
    ) {
      this.initImages();
    }

    if (plainGalleryConfigChange) {
      // const prevPlainGalleryConfigChange: any = plainGalleryConfigChange.previousValue;
      const currPlainGalleryConfigChange: PlainGalleryConfig =
        plainGalleryConfigChange.currentValue;
      if (
        currPlainGalleryConfigChange.layout &&
        currPlainGalleryConfigChange.layout instanceof AdvancedLayout &&
        currPlainGalleryConfigChange.layout.modalOpenerByIndex !== -1
      ) {
        // console.log('opening modal gallery from custom plain gallery, index: ', currPlainGalleryConfigChange);
        this.showModalGallery(
          currPlainGalleryConfigChange.layout.modalOpenerByIndex
        );
      }
    }
  }

  /**
   * Method ´ngOnInit´ to init images calling `initImages()`.
   * This is an Angular's lifecycle hook, so its called automatically by Angular itself.
   * In particular, it's called only one time!!!
   */
  ngOnInit() {
    console.log("modal, userme", this.user);
    // console.log("modal, commentsDataSource", this.commentsDataSource);
    this.idValidatorService.checkAndAdd(this.id);

    // id is a mandatory input and must a number > 0
    if ((!this.id && this.id !== 0) || this.id < 0) {
      throw new Error(
        `'[id]="a number >= 0"' is a mandatory input from 6.0.0 in angular-modal-gallery.` +
          `If you are using multiple instances of this library, please be sure to use different ids`
      );
    }

    // call initImages to init images and to emit `hasData` event
    this.initImages();

    const defaultSlideConfig: SlideConfig = {
      infinite: false,
      playConfig: <PlayConfig>{
        autoPlay: false,
        interval: 5000,
        pauseOnHover: true,
      },
      sidePreviews: <SidePreviewsConfig>{
        show: true,
        size: { width: "100px", height: "auto" },
      },
    };
    this.configSlide = Object.assign({}, defaultSlideConfig, this.slideConfig);

    this.galleryServiceNavigateSubscription =
      this.galleryService.navigate.subscribe(
        (payload: InternalGalleryPayload) => {
          if (!payload) {
            return;
          }
          // if galleryId is not valid OR galleryId is related to another instance and not this one
          if (
            payload.galleryId === undefined ||
            payload.galleryId < 0 ||
            payload.galleryId !== this.id
          ) {
            return;
          }
          // if image index is not valid
          if (payload.index < 0 || payload.index > this.images.length) {
            return;
          }
          this.showModalGallery(payload.index, true);
        }
      );

    this.galleryServiceCloseSubscription = this.galleryService.close.subscribe(
      (galleryId: number) => {
        if (galleryId < 0 || this.id !== galleryId) {
          return;
        }
        this.closeGallery(Action.NORMAL, true);
      }
    );

    this.galleryServiceUpdateSubscription =
      this.galleryService.update.subscribe(
        (payload: InternalGalleryPayload) => {
          if (!payload) {
            return;
          }
          // if galleryId is not valid OR galleryId is related to another instance and not this one
          if (
            payload.galleryId === undefined ||
            payload.galleryId < 0 ||
            payload.galleryId !== this.id
          ) {
            return;
          }
          // if either image index or image are not valid
          if (
            payload.index < 0 ||
            payload.index > this.images.length ||
            !payload.image
          ) {
            return;
          }
          const currentIndex: number = getIndex(payload.image, this.images);
          this.images = this.images.map(
            (image: InternalLibImage, index: number) => {
              if (image.id === payload.index) {
                return <InternalLibImage>payload.image;
              }
              return image;
            }
          );
          if (currentIndex === payload.index) {
            this.currentImage = this.images[payload.index];
          }
          this.changeDetectorRef.markForCheck();
        }
      );

    this.galleryServiceAutoPlaySubscription =
      this.galleryService.autoPlay.subscribe(
        (payload: InternalGalleryPayload) => {
          // if galleryId is not valid OR galleryId is related to another instance and not this one
          if (
            payload.galleryId === undefined ||
            payload.galleryId < 0 ||
            payload.galleryId !== this.id
          ) {
            return;
          }
          this.configSlide.playConfig.autoPlay = payload.result;
        }
      );
  }

  /**
   * Method called by custom upper buttons.
   * @param ButtonEvent event payload
   */
  onCustomEmit(event: ButtonEvent) {
    const eventToEmit: ButtonEvent = this.getButtonEventToEmit(event);
    this.buttonBeforeHook.emit(eventToEmit);
    // console.log('on onCustomEmit', eventToEmit);
    this.buttonAfterHook.emit(eventToEmit);
  }

  /**
   * Реакция на нажатие кнопки информации о фото
   * @param ButtonEvent event payload
   */
  onShowInfo(event: ButtonEvent) {
    const eventToEmit: ButtonEvent = this.getButtonEventToEmit(event);

    this.buttonBeforeHook.emit(eventToEmit);

    if (this.openedInfoBlock) {
      if (this.infoBlockContent === event.button.type) {
        this.openedInfoBlock = false;
      } else {
        this.infoBlockContent = ButtonType.INFO;
      }
    } else {
      this.infoBlockContent = ButtonType.INFO;
      this.openedInfoBlock = true;
    }

    this.info.emit(event);

    this.buttonAfterHook.emit(eventToEmit);
  }

  // TODO implement on refresh
  // /**
  //  * Method called by the refresh upper button.
  //  * STILL NOT IMPLEMENTED, SO DON'T USE IT
  //  * @param ButtonEvent event payload
  //  */
  // onRefresh(event: ButtonEvent) {
  //   const eventToEmit: ButtonEvent = this.getButtonEventToEmit(event);
  //
  //   this.buttonBeforeHook.emit(eventToEmit);
  //   // console.log('TODO implement on refresh inside the library', eventToEmit);
  //
  //   this.currentImage = Object.assign({}, this.currentImage, { previouslyLoaded: false });
  //
  //   // TODO add logic to hide and show the current image
  //
  //   // console.log('onRefresh', this.currentImage);
  //
  //   // const indexNum: number = this.currentImageComponent.getIndex();
  //
  //   // this.images = this.images.map((val: InternalLibImage, index: number) => {
  //   //   if (index !== 2) {
  //   //     return val;
  //   //   } else {
  //   //     const img: InternalLibImage = Object.assign({}, val, {previouslyLoaded: false});
  //   //     return img;
  //   //   }
  //   // });
  //   //
  //   // this.closeGallery();
  //   // this.showModalGallery(2);
  //
  //   this.buttonAfterHook.emit(eventToEmit);
  // }

  /**
   * Method called by show comments button.
   * @param ButtonEvent event payload
   */
  onShowComments(event: ButtonEvent) {
    const eventToEmit: ButtonEvent = this.getButtonEventToEmit(event);

    this.buttonBeforeHook.emit(eventToEmit);

    if (this.openedInfoBlock) {
      if (this.infoBlockContent === event.button.type) {
        this.openedInfoBlock = false;
      } else {
        this.infoBlockContent = ButtonType.COMMENTS;
      }
    } else {
      this.infoBlockContent = ButtonType.COMMENTS;
      this.openedInfoBlock = true;
    }

    this.buttonAfterHook.emit(eventToEmit);
  }

  /**
   * Method called by the rotate upper button.
   * @param ButtonEvent event payload
   */
  onRotate(event: ButtonEvent) {
    const eventToEmit: ButtonEvent = this.getButtonEventToEmit(event);
    this.buttonBeforeHook.emit(eventToEmit);

    // Устанавливаем дефолтное значение угла = 0.
    if (!eventToEmit.image.modal.angle) {
      eventToEmit.image.modal.angle = 0;
    }

    // Вычисляем угол поворота в соответствии с типом нажатой кнопки.
    switch (eventToEmit.button.type) {
      case 7: {
        eventToEmit.image.modal.angle =
          (eventToEmit.image.modal.angle + 360 - 90) % 360;
        break;
      }
      case 8: {
        eventToEmit.image.modal.angle =
          (eventToEmit.image.modal.angle + 360 + 90) % 360;
        break;
      }
    }

    // Т.к. стратегия используется OnPush, создаем новый объект текущего изображения.
    this.currentImage = Object.assign({}, this.currentImage);

    //
    // console.log(event)
    //
    // const canv = document.createElement('canvas');
    // let ctx;
    // canv.id = 'someId';
    // // canv.style.display = 'none';
    //
    // var image = document.createElement("img");
    // image.src = eventToEmit.image.modal.img;
    // image.onload=function(){
    //   let rotatedDrawImage = (ctx, canvas, image, angle) => {
    //     ctx.clearRect(0,0,canvas.width,canvas.height);
    //     ctx.save();
    //     ctx.translate(canvas.width/2,canvas.height/2);
    //     ctx.rotate(angle*Math.PI/180);
    //     ctx.drawImage(image,-image.width/2,-image.width/2);
    //     ctx.restore();
    //   }
    //
    //   canv.width = image.height;
    //   canv.height = image.width;
    //
    //   document.body.appendChild(canv); // adds the canvas to the body element
    //
    //   const canvas = <HTMLCanvasElement> document.getElementById("someId");
    //
    //   ctx = canvas.getContext("2d");
    //
    //   ctx.drawImage(image,canvas.width/2-image.width/2,canvas.height/2-image.width/2);
    //   // ctx.drawImage(image,canvas.width/2-image.width/2,canvas.height/2-image.width/2);
    //
    //   rotatedDrawImage(ctx, canvas, image, 90)
    // }
    //
    //
    //

    //
    // const ctx = canvas.getContext("2d");
    //
    // ctx.drawImage(eventToEmit.image,canvas.width/2-eventToEmit.image.width/2,canvas.height/2-eventToEmit.image.width/2);
    //

    // TODO implement rotation logic

    this.buttonAfterHook.emit(eventToEmit);
  }

  onDoubleSpeed(event: ButtonEvent) {
    switch (this.currentImage.type) {
      case "video": {
        if (this.modalGalleryService.speedVideo$.getValue() === 1) {
          this.modalGalleryService.onDoubleVideoSpeed();
        } else {
          this.modalGalleryService.resetVideoSpeed();
        }
        break;
      }
      case "audio": {
        if (this.modalGalleryService.speedAudio$.getValue() === 1) {
          this.modalGalleryService.onDoubleAudioSpeed();
        } else {
          this.modalGalleryService.resetAudioSpeed();
        }
        break;
      }
    }
  }

  /**
   * Method called by the rotateCss upper button.
   * @param ButtonEvent event payload
   */
  onRotateCss(event: ButtonEvent) {
    //console.log("onRotateCss",event)
    let image = this.currentImage;
    if (!image) return;
    this.RotateImage(image, event.button.type);
    const eventToEmit: ButtonEvent = this.getButtonEventToEmitCSS(event);

    this.buttonBeforeHook.emit(eventToEmit);
    this.buttonAfterHook.emit(eventToEmit);
  }

  RotateImage(image, idButton) {
    let imageFromArray = this.getImageByIdFromImages(image.id);
    if (idButton == 12) {
      image.angle = (image.angle + 360 + 90) % 360;
      imageFromArray.angle = (imageFromArray.angle + 360 + 90) % 360;
      if (imageFromArray.angle == 360) {
        image.angle = 0;
        imageFromArray.angle = 0;
      }
    }
    if (idButton == 11) {
      image.angle = (image.angle + 360 - 90) % 360;
      imageFromArray.angle = (imageFromArray.angle + 360 - 90) % 360;
      if (imageFromArray.angle == -90) {
        image.angle = 270;
        imageFromArray.angle = 270;
      }
    }
  }

  /**
   * Method called by the full-screen upper button.
   * @param ButtonEvent event payload
   */
  onFullScreen(event: ButtonEvent) {
    const eventToEmit: ButtonEvent = this.getButtonEventToEmit(event);
    this.buttonBeforeHook.emit(eventToEmit);

    const doc: any = <any>document;
    const docEl: any = <any>document.documentElement;

    const fullscreenDisabled: boolean =
      !doc.fullscreenElement &&
      !doc.webkitFullscreenElement &&
      !doc.mozFullScreenElement &&
      !doc.msFullscreenElement;

    if (fullscreenDisabled) {
      if (docEl.requestFullscreen) {
        docEl.requestFullscreen();
      } else if (docEl.webkitRequestFullscreen) {
        docEl.webkitRequestFullscreen();
      } else if (docEl.mozRequestFullScreen) {
        docEl.mozRequestFullScreen();
      } else if (docEl.msRequestFullscreen) {
        docEl.msRequestFullscreen();
      }
    } else {
      if (doc.exitFullscreen) {
        doc.exitFullscreen();
      } else if (doc.msExitFullscreen) {
        doc.msExitFullscreen();
      } else if (doc.mozCancelFullScreen) {
        doc.mozCancelFullScreen();
      } else if (doc.webkitExitFullscreen) {
        doc.webkitExitFullscreen();
      }
    }

    this.buttonAfterHook.emit(eventToEmit);
  }

  /**
   * Method called by the delete upper button.
   * @param ButtonEvent event payload
   */
  onDelete(event: ButtonEvent) {
    const eventToEmit: ButtonEvent = this.getButtonEventToEmit(event);
    this.buttonBeforeHook.emit(eventToEmit);

    if (this.images.length === 1) {
      this.closeGallery();
    }

    const imageIndexToDelete: number =
      this.currentImageComponent.getIndexToDelete(event.image);
    if (imageIndexToDelete === this.images.length - 1) {
      // last image
      this.currentImageComponent.prevImage();
    } else {
      this.currentImageComponent.nextImage();
    }

    this.buttonAfterHook.emit(eventToEmit);
  }

  /**
   * Method called by the navigate upper button.
   * @param ButtonEvent event payload
   */
  onNavigate(event: ButtonEvent) {
    const eventToEmit: ButtonEvent = this.getButtonEventToEmit(event);
    this.buttonBeforeHook.emit(eventToEmit);
    // To support SSR
    if (isPlatformBrowser(this.platformId)) {
      if (eventToEmit.image && eventToEmit.image.modal.extUrl) {
        // where I should open this link? The current tab or another one?
        if (eventToEmit.button && eventToEmit.button.extUrlInNewTab) {
          // in this case I should use target _blank to open the url in a new tab, however these is a security issue.
          // Prevent Reverse Tabnabbing's attacks (https://www.owasp.org/index.php/Reverse_Tabnabbing)
          // Some resources:
          // - https://www.owasp.org/index.php/HTML5_Security_Cheat_Sheet#Tabnabbing
          // - https://medium.com/@jitbit/target-blank-the-most-underestimated-vulnerability-ever-96e328301f4c
          // - https://developer.mozilla.org/en-US/docs/Web/API/Window/open
          const newWindow = window.open(
            eventToEmit.image.modal.extUrl,
            "noopener,noreferrer,"
          );
          newWindow.opener = null; // required to prevent security issues
        } else {
          window.location.href = eventToEmit.image.modal.extUrl;
        }
      }
    }
    this.buttonAfterHook.emit(eventToEmit);
  }

  /**
   * Method called by the download upper button.
   * @param ButtonEvent event payload
   */
  onDownload(event: ButtonEvent) {
    const eventToEmit: ButtonEvent = this.getButtonEventToEmit(event);
    this.buttonBeforeHook.emit(eventToEmit);
    this.downloadImage();
    this.buttonAfterHook.emit(eventToEmit);
  }

  /**
   * Method called by the close upper button.
   * @param ButtonEvent event payload
   * @param Action action that triggered the close method. `Action.NORMAL` by default
   */
  onCloseGallery(event: ButtonEvent, action: Action = Action.NORMAL) {
    const eventToEmit: ButtonEvent = this.getButtonEventToEmit(event);
    this.buttonBeforeHook.emit(eventToEmit);
    this.closeGallery(action);
    this.buttonAfterHook.emit(eventToEmit);
  }

  /**
   * Method to close the modal gallery specifying the action.
   * It also reset the `keyboardService` to prevent multiple listeners.
   * @param Action action type. `Action.NORMAL` by default
   * @param boolean isCalledByService is true if called by gallery.service, otherwise false
   */
  closeGallery(
    action: Action = Action.NORMAL,
    isCalledByService: boolean = false
  ) {
    // Move elements back from body before Angular destroys the *ngIf view
    this.moveBackFromPortal();

    this.close.emit(new ImageModalEvent(action, true));
    this.opened = false;
    this.keyboardService.reset();

    // чтобы сбрасывался класс rotate
    let image = this.getImageByIdFromImages(this.currentImage.id);
    image.angle = 0;
    if (this.currentImage.modal && this.currentImage.modal.angle) {
      this.currentImage.modal.angle = undefined;
    }

    // shows scrollbar
    document.body.style.overflow = "visible";

    if (isCalledByService) {
      // the following is required, otherwise the view will not be updated
      // this happens only if called by gallery.service
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Method called when you click on an image of your plain (or inline) gallery.
   * @param number index of the clicked image
   */
  onShowModalGallery(index: number) {
    this.showModalGallery(index);
  }

  /**
   * Method to show the modal gallery displaying the image with
   * the index specified as input parameter.
   * It will also register a new `keyboardService` to catch keyboard's events to download the current
   * image with keyboard's shortcuts. This service, will be removed either when modal gallery component
   * will be destroyed or when the gallery is closed invoking the `closeGallery` method.
   * @param number index of the image to show
   * @param boolean isCalledByService is true if called by gallery.service, otherwise false
   */
  showModalGallery(index: number, isCalledByService: boolean = false) {
    // hides scrollbar
    document.body.style.overflow = "hidden";

    this.keyboardService.add((event: KeyboardEvent, combo: string) => {
      if (event.preventDefault) {
        event.preventDefault();
      } else {
        // internet explorer
        event.returnValue = false;
      }
      this.downloadImage();
    });

    this.opened = true;
    // console.log("showModalGallery",this.images,index)
    this.currentImage = this.images[index];

    // emit a new ImageModalEvent with the index of the current image
    this.show.emit(new ImageModalEvent(Action.LOAD, index + 1));

    if (isCalledByService) {
      // the following is required, otherwise the view will not be updated
      // this happens only if called by gallery.service
      this.changeDetectorRef.markForCheck();
    }

    // Safari fix: move overlay and modal wrapper to document.body
    // so they escape any ancestor stacking-context / overflow clipping
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => this.moveToPortal());
    }
  }

  /**
   * Method called when the image changes and used to update the `currentImage` object.
   * @param ImageModalEvent event payload
   */
  onChangeCurrentImage(event: ImageModalEvent) {
    const newIndex: number = <number>event.result;
    if (newIndex < 0 || newIndex >= this.images.length) {
      return;
    }

    let image = this.getImageByIdFromImages(this.currentImage.id);
    image.angle = 0;

    // чтобы сбрасывался класс rotate
    if (this.currentImage.modal && this.currentImage.modal.angle) {
      this.currentImage.modal.angle = undefined;
    }

    this.currentImage = this.images[newIndex];

    // emit first/last event based on newIndex value
    this.emitBoundaryEvent(event.action, newIndex);

    //закрыть компаре фото
    this.currentImageComponent.closeCompare();

    // emit current visible image index
    this.show.emit(new ImageModalEvent(event.action, newIndex + 1));
  }

  isPlainGalleryVisible(): boolean {
    if (
      this.plainGalleryConfig &&
      this.plainGalleryConfig.layout &&
      this.plainGalleryConfig.layout instanceof AdvancedLayout
    ) {
      return !this.plainGalleryConfig.layout.hideDefaultPlainGallery;
    }
    return true;
  }

  /**
   * Method called when you click 'outside' (i.e. on the semi-transparent background)
   * to close the modal gallery if `enableCloseOutside` is true.
   * @param boolean event payload. True to close the modal gallery, false otherwise
   */
  onClickOutside(event: boolean) {
    if (event && this.enableCloseOutside) {
      this.closeGallery(Action.CLICK);
    }
  }

  /**
   * Method called when an image is loaded and the loading spinner has gone.
   * It sets the previouslyLoaded flag inside the Image to hide loading spinner when displayed again.
   * @param ImageLoadEvent event payload
   */
  onImageLoad(event: ImageLoadEvent) {
    // console.log('modal-image onImageLoad', event);
    // console.log('modal-image onImageLoad images before', this.images);

    // sets as previously loaded the image with index specified by `event.status`
    this.images = this.images.map((img: InternalLibImage) => {
      if (img && img.id === event.id) {
        return Object.assign({}, img, { previouslyLoaded: event.status });
      }
      return img;
    });

    // console.log('modal-image onImageLoad images after', this.images);
  }

  /**
   * Method called when a dot is clicked and used to update the current image.
   * @param number index of the clicked dot
   */
  onClickDot(index: number) {
    this.currentImage = this.images[index];
  }

  /**
   * Method called when an image preview is clicked and used to update the current image.
   * @param Image preview image
   */
  onClickPreview(event: ImageModalEvent) {
    this.onChangeCurrentImage(event);
  }

  // onClickArrow(event: InteractionEvent) {
  //   // TODO validate before to emit
  //   this.arrow.emit(event);
  // }

  /**
   * Method to download the current image, only if `downloadable` is true.
   * It contains also a logic to enable downloading features also for IE11.
   */
  downloadImage() {
    if (this.currentImageConfig && !this.currentImageConfig.downloadable) {
      return;
    }
    this.downloadImageAllBrowsers();
  }

  /**
   * Method to cleanup resources. In fact, this will reset keyboard's service.
   * This is an Angular's lifecycle hook that is called when this component is destroyed.
   */
  ngOnDestroy() {
    // Move portal elements back so Angular can clean up properly
    this.moveBackFromPortal();

    this.keyboardService.reset();

    this.idValidatorService.remove(this.id);

    if (this.galleryServiceNavigateSubscription) {
      this.galleryServiceNavigateSubscription.unsubscribe();
    }
    if (this.galleryServiceCloseSubscription) {
      this.galleryServiceCloseSubscription.unsubscribe();
    }
    if (this.galleryServiceUpdateSubscription) {
      this.galleryServiceUpdateSubscription.unsubscribe();
    }
    if (this.galleryServiceAutoPlaySubscription) {
      this.galleryServiceAutoPlaySubscription.unsubscribe();
    }

    // Remove portal container from body
    if (this.portalContainer && this.portalContainer.parentNode) {
      this.portalContainer.parentNode.removeChild(this.portalContainer);
      this.portalContainer = null;
    }
  }

  /**
   * Creates (once) a thin wrapper div on document.body and returns it.
   * The wrapper itself has no visual footprint – it only serves as
   * a DOM home for the overlay and the modal-gallery-wrapper so they
   * are NOT nested inside any ancestor that creates a stacking context
   * or overflow clipping (which breaks position:fixed in Safari).
   */
  private ensurePortalContainer(): HTMLElement {
    if (!this.portalContainer) {
      this.portalContainer = document.createElement('div');
      this.portalContainer.className = 'ks-modal-gallery-portal';
      document.body.appendChild(this.portalContainer);
    }
    return this.portalContainer;
  }

  /**
   * Move the overlay (<ks-background>) and the modal wrapper
   * (#modal-gallery-wrapper) from the component host into the
   * portal container on <body>.
   */
  private moveToPortal(): void {
    const container = this.ensurePortalContainer();
    const host: HTMLElement = this.elRef.nativeElement;

    const bg = host.querySelector('ks-background');
    const wrapper = host.querySelector('#modal-gallery-wrapper');

    if (bg) { container.appendChild(bg); }
    if (wrapper) { container.appendChild(wrapper); }
  }

  /**
   * Move the overlay and modal wrapper back into the component host
   * so that Angular can properly tear-down the *ngIf view.
   */
  private moveBackFromPortal(): void {
    if (!this.portalContainer) { return; }

    const host: HTMLElement = this.elRef.nativeElement;
    const bg = this.portalContainer.querySelector('ks-background');
    const wrapper = this.portalContainer.querySelector('#modal-gallery-wrapper');

    if (bg) { host.appendChild(bg); }
    if (wrapper) { host.appendChild(wrapper); }
  }

  checkboxChange(event) {
    this.selectImage.emit(event);
  }

  showComments() {
    console.log("showComments-deep-w");
    this.infoBlockContent = ButtonType.COMMENTS;
    this.openedInfoBlock = true;
  }

  closeComments() {
    if (this.infoBlockContent == ButtonType.COMMENTS)
      this.openedInfoBlock = false;
  }

  getImageByIdFromImages(id) {
    return this.images.find((item) => item.id == id);
  }

  getImageByIdFromModalImages(id) {
    return this.modalImages.find((item) => item.id == id);
  }

  longTask(comment: IComment) {
    this.longerTask.emit(comment);
  }

  /**
   * Private method to download the current image for all browsers.
   * @private
   */
  private downloadImageAllBrowsers(): void {
    const link = document.createElement("a");
    let isBase64 = false;
    let img: string;
    // convert a SafeResourceUrl to a string
    if (typeof this.currentImage.modal.img === "string") {
      img = this.currentImage.modal.img as string;
    } else {
      // if it's a SafeResourceUrl
      img = this.sanitizer.sanitize(
        SecurityContext.RESOURCE_URL,
        this.currentImage.modal.img
      ) as string;
    }
    if (img.includes("data:image/") || img.includes(";base64,")) {
      const extension: string = img
        .replace("data:image/", "")
        .split(";base64,")[0];
      const pureBase64: string = img.split(";base64,")[1];
      const blob: Blob = this.base64toBlob(pureBase64, "image/" + extension);
      link.href = URL.createObjectURL(blob);
      isBase64 = true;
      link.setAttribute(
        "download",
        this.getFileName(this.currentImage, isBase64, extension)
      );
    } else {
      link.href = img;
      link.setAttribute(
        "download",
        this.getFileName(this.currentImage, isBase64)
      );
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Method to convert a base64 to a Blob
   * @param base64Data string with base64 data
   * @param contentType string with the MIME type
   * @return Blob converted from the input base64Data
   * @private
   */
  private base64toBlob(base64Data: string, contentType: string = ""): Blob {
    const sliceSize = 1024;
    const byteCharacters: string = atob(base64Data);
    const bytesLength: number = byteCharacters.length;
    const slicesCount: number = Math.ceil(bytesLength / sliceSize);
    const byteArrays: Array<Uint8Array> = new Array(slicesCount);
    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin: number = sliceIndex * sliceSize;
      const end: number = Math.min(begin + sliceSize, bytesLength);
      const bytes: Array<number> = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  /**
   * Private method to get the `ButtonEvent` to emit, merging the input `ButtonEvent`
   * with the current image.
   * @param ButtonEvent event payload to return
   * @returns ButtonEvent event payload with the current image included
   */
  private getButtonEventToEmit(event: ButtonEvent): ButtonEvent {
    return Object.assign(event, { image: this.currentImage });
  }

  /**
   * Private method to get the `ButtonEvent` to emit, merging the input `ButtonEvent`
   * with the current image.
   * @param ButtonEvent event payload to return
   * @returns ButtonEvent event payload with the current image included
   */
  private getButtonEventToEmitCSS(event: ButtonEvent): ButtonEvent {
    return Object.assign(event, {
      image: this.getImageByIdFromImages(this.currentImage.id),
    });
  }

  /**
   * Private method to get the file name from an input path.
   * This is used either to get the image's name from its path or from the Image itself,
   * if specified as 'downloadFileName' by the user.
   * @param image Image image to extract its file name
   * @param isBase64 boolean to set if the image is a base64 file or not. False by default.
   * @param base64Extension string to force the extension of the base64 image. Empty string by default.
   * @returns string string file name of the input image.
   * @private
   */
  private getFileName(
    image: Image,
    isBase64: boolean = false,
    base64Extension: string = ""
  ): string {
    if (
      !image.modal.downloadFileName ||
      image.modal.downloadFileName.length === 0
    ) {
      if (isBase64) {
        return `Image-${image.id}.${
          base64Extension !== "" ? base64Extension : "png"
        }`;
      } else {
        return (image.modal.img as string).replace(/^.*[\\\/]/, "");
      }
    } else {
      return image.modal.downloadFileName;
    }
  }

  /**
   * Private method to initialize `images` as array of `Image`s.
   * Also, it will emit ImageowmodaModalEvent to say that images are loaded.
   */
  private initImages() {
    // I'm not cloning the array, but I'm doing this to cast it to an array of InternalLibImages
    this.images = <InternalLibImage[]>this.modalImages;
    this.hasData.emit(new ImageModalEvent(Action.LOAD, true));
    this.showGallery = this.images.length > 0;
  }

  /**
   * Private method to emit events when either the last or the first image are visible.
   * @param action Enum of type Action that represents the source of the event that changed the
   *  current image to the first one or the last one.
   * @param indexToCheck is the index number of the image (the first or the last one).
   */
  private emitBoundaryEvent(action: Action, indexToCheck: number) {
    // to emit first/last event
    switch (indexToCheck) {
      case 0:
        this.firstImage.emit(new ImageModalEvent(action, true));
        break;
      case this.images.length - 1:
        this.lastImage.emit(new ImageModalEvent(action, true));
        break;
    }
  }
}
